import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { installBodyPointerEventsGuard } from './utils/radixPointerGuard';

// Kör direkt vid modul-load så vakten är på plats innan första render
installBodyPointerEventsGuard();

import LandingPage from './pages/LandingPage';
import OmOssPage from './pages/OmOssPage';
import ContactPage from './pages/ContactPage';
import ByggKalkylPage from './pages/ByggKalkylPage';
import TidrapporteringPage from './pages/TidrapporteringPage';
import PrisPage from './pages/PrisPage';
import BloggPage from './pages/BloggPage';
import BlogPostPage from './pages/BlogPostPage';
import LoginPage from './pages/LoginPage';
import WorkerLoginPage from './pages/WorkerLoginPage';
import RegisterPage from './pages/RegisterPage';
import RegisterSuccessPage from './pages/RegisterSuccessPage';
import BillingPage from './pages/BillingPage';
import BillingSuccessPage from './pages/BillingSuccessPage';
import IntegritetspolicyPage from './pages/IntegritetspolicyPage';
import AdminLayout from './layouts/AdminLayout';
import WorkerLayout from './layouts/WorkerLayout';
import Dashboard from './pages/admin/Dashboard';
import Projects from './pages/admin/Projects';
import ProjectDetail from './pages/admin/ProjectDetail';
import Employees from './pages/admin/Employees';
import Machines from './pages/admin/Machines';
import InventoryPage from './pages/admin/InventoryPage';
import TimeReports from './pages/admin/TimeReports';
import MachineHours from './pages/admin/MachineHours';
import ETAReports from './pages/admin/ETAReports';
import ETADetail from './pages/admin/ETADetail';
import Daybooks from './pages/admin/Daybooks';
import DaybookDetail from './pages/admin/DaybookDetail';
import InvoiceDrafts from './pages/admin/InvoiceDrafts';
import Tenders from './pages/admin/Tenders';
import TenderDetail from './pages/admin/TenderDetail';
import Suppliers from './pages/admin/Suppliers';
import CompareOffer from './pages/admin/CompareOffer';
import SupportChat from './pages/admin/SupportChat';
import CalendarPage from './pages/admin/CalendarPage';
import SuperAdminLayout from './layouts/SuperAdminLayout';
import SuperAdminDashboard from './pages/superadmin/SuperAdminDashboard';
import SuperAdminCompanies from './pages/superadmin/SuperAdminCompanies';
import SuperAdminUsers from './pages/superadmin/SuperAdminUsers';
import SuperAdminLiveChat from './pages/superadmin/SuperAdminLiveChat';
import SuperAdminPublicChats from './pages/superadmin/SuperAdminPublicChats';
import SuperAdminSales from './pages/superadmin/SuperAdminSales';
import SuperAdminSalesStats from './pages/superadmin/SuperAdminSalesStats';
import SuperAdminSalesTeam from './pages/superadmin/SuperAdminSalesTeam';
import SalesLayout from './layouts/SalesLayout';
import SalesDashboard from './pages/sales/SalesDashboard';
import SalesCustomers from './pages/sales/SalesCustomers';
import SalesNewCustomer from './pages/sales/SalesNewCustomer';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import NotFoundPage from './pages/NotFoundPage';
import SuperAdminSupportAgents from './pages/superadmin/SuperAdminSupportAgents';
import SuperAdminActivityMap from './pages/superadmin/SuperAdminActivityMap';
import SuperAdminHealth from './pages/superadmin/SuperAdminHealth';
import CustomerProjectView from './pages/customer/CustomerProjectView';
import CustomerLogin from './pages/customer/CustomerLogin';
import CustomerPortal from './pages/customer/CustomerPortal';
import CustomerPortalDetail from './pages/customer/CustomerPortalDetail';
import WorkerToday from './pages/worker/WorkerToday';
import WorkerHistory from './pages/worker/WorkerHistory';
import PayrollPage from './pages/admin/PayrollPage';
import PayrollRunsPage from './pages/admin/PayrollRunsPage';
import WorkerPayslips from './pages/worker/WorkerPayslips';
import DataImportPage from './pages/admin/DataImportPage';
import InternalChatPage from './pages/admin/InternalChatPage';
import AccountingPage from './pages/admin/AccountingPage';
import SecuritySettings from './pages/admin/SecuritySettings';
import SettingsPage from './pages/admin/SettingsPage';
import QuickCalcArchive from './pages/admin/QuickCalcArchive';
import WorkerProjects from './pages/worker/WorkerProjects';
import WorkerReportTime from './pages/worker/WorkerReportTime';
import WorkerReportETA from './pages/worker/WorkerReportETA';
import WorkerChat from './pages/worker/WorkerChat';
import WorkerPersonalTime from './pages/worker/WorkerPersonalTime';
import WorkerSchedule from './pages/worker/WorkerSchedule';
import Customers from './pages/admin/Customers';
import BillingProfiles from './pages/admin/BillingProfiles';
import Schedule from './pages/admin/Schedule';
import { AuthProvider, useAuth } from './context/AuthContext';

const PrivateRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Laddar...</div>;
  }

  if (!user) {
    // Redirect to appropriate login page based on expected role
    if (allowedRoles && allowedRoles.includes('worker')) {
      return <Navigate to="/worker/login" />;
    }
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    if (user.role === 'worker') return <Navigate to="/worker" />;
    if (user.role === 'superadmin') return <Navigate to="/superadmin" />;
    return <Navigate to="/dashboard" />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();

  // Iter50: När användaren är inloggad på native-appen → registrera FCM push-token
  React.useEffect(() => {
    if (!user) return;
    let cleanup = () => {};
    (async () => {
      try {
        const { native } = await import('./utils/native');
        if (!native.isNative) return;
        // Sätt statusbar-färg och tunable haptics
        await native.setStatusBar('#E8613A', 'LIGHT');
        // Registrera push
        const reg = await native.push.register({
          onToken: async (token) => {
            try {
              const { api } = await import('./utils/api');
              await api.registerFcmToken({
                token,
                platform: native.platform,
                device_name: navigator.userAgent.slice(0, 100),
              });
            } catch (e) {
              console.warn('FCM register failed:', e);
            }
          },
          onReceived: (notif) => {
            console.info('[Push]', notif);
          },
          onAction: (action) => {
            const url = action?.notification?.data?.click_url;
            if (url) window.location.href = url;
          },
        });
        cleanup = reg.unregister;
      } catch (e) {
        console.warn('Native init failed:', e);
      }
    })();
    return () => cleanup();
  }, [user]);

  return (
    <Routes>
      {/* Public Landing Page */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/about" element={<OmOssPage />} />
      <Route path="/kontakt" element={<ContactPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/bygg-kalkyl-program" element={<ByggKalkylPage />} />
      <Route path="/tidrapportering-bygg" element={<TidrapporteringPage />} />
      <Route path="/priser" element={<PrisPage />} />
      <Route path="/pricing" element={<PrisPage />} />
      <Route path="/blogg" element={<BloggPage />} />
      <Route path="/blogg/:slug" element={<BlogPostPage />} />
      <Route path="/integritetspolicy" element={<IntegritetspolicyPage />} />
      <Route path="/privacy" element={<IntegritetspolicyPage />} />
      <Route path="/privacy-policy" element={<IntegritetspolicyPage />} />
      <Route path="/glomt-losenord" element={<ForgotPasswordPage />} />
      <Route path="/aterstall-losenord" element={<ResetPasswordPage />} />

      <Route path="/login" element={<LoginPage />} />
      <Route path="/worker/login" element={<WorkerLoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/register/success" element={<RegisterSuccessPage />} />
      <Route path="/billing" element={<BillingPage />} />
      <Route path="/billing/success" element={<BillingSuccessPage />} />
      <Route path="/billing/cancel" element={<BillingPage />} />

      {/* Customer Portal (Public) */}
      <Route path="/customer/project/:token" element={<CustomerProjectView />} />
      <Route path="/customer/login" element={<CustomerLogin />} />
      <Route path="/customer/portal" element={<CustomerPortal />} />
      <Route path="/customer/portal/project/:projectId" element={<CustomerPortalDetail />} />

      {/* Super Admin Routes */}
      <Route
        path="/superadmin"
        element={
          <PrivateRoute allowedRoles={['superadmin']}>
            <SuperAdminLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<SuperAdminDashboard />} />
        <Route path="companies" element={<SuperAdminCompanies />} />
        <Route path="users" element={<SuperAdminUsers />} />
        <Route path="live-chat" element={<SuperAdminLiveChat />} />
        <Route path="public-chats" element={<SuperAdminPublicChats />} />
        <Route path="sales" element={<SuperAdminSales />} />
        <Route path="ny-kund" element={<SuperAdminSales />} />
        <Route path="sales-stats" element={<SuperAdminSalesStats />} />
        <Route path="sales-team" element={<SuperAdminSalesTeam />} />
        <Route path="support-agents" element={<SuperAdminSupportAgents />} />
        <Route path="activity-map" element={<SuperAdminActivityMap />} />
        <Route path="health" element={<SuperAdminHealth />} />
      </Route>

      {/* Admin Routes */}
      <Route
        path="/dashboard"
        element={
          <PrivateRoute allowedRoles={['admin']}>
            <AdminLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:id" element={<ProjectDetail />} />
        <Route path="employees" element={<Employees />} />
        <Route path="machines" element={<Machines />} />
        <Route path="inventory" element={<InventoryPage />} />
        <Route path="time-reports" element={<TimeReports />} />
        <Route path="machine-hours" element={<MachineHours />} />
        <Route path="eta" element={<ETAReports />} />
        <Route path="eta/:id" element={<ETADetail />} />
        <Route path="daybook" element={<Daybooks />} />
        <Route path="daybook/:id" element={<DaybookDetail />} />
        <Route path="invoices" element={<InvoiceDrafts />} />
        <Route path="customers" element={<Customers />} />
        <Route path="billing-profiles" element={<BillingProfiles />} />
        <Route path="schedule" element={<Schedule />} />
        <Route path="tenders" element={<Tenders />} />
        <Route path="tenders/:id" element={<TenderDetail />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="compare-offer" element={<CompareOffer />} />
        <Route path="support-chat" element={<SupportChat />} />
        <Route path="payroll" element={<PayrollPage />} />
        <Route path="payroll-runs" element={<PayrollRunsPage />} />
        <Route path="import" element={<DataImportPage />} />
        <Route path="accounting" element={<AccountingPage />} />
        <Route path="chat" element={<InternalChatPage />} />
        <Route path="security" element={<SecuritySettings />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="snabb-arkiv" element={<QuickCalcArchive />} />
      </Route>

      {/* Worker Routes */}
      <Route
        path="/worker"
        element={
          <PrivateRoute allowedRoles={['worker']}>
            <WorkerLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<WorkerToday />} />
        <Route path="projects" element={<WorkerProjects />} />
        <Route path="report-time" element={<WorkerReportTime />} />
        <Route path="report-eta" element={<WorkerReportETA />} />
        <Route path="chat" element={<WorkerChat />} />
        <Route path="personal-time" element={<WorkerPersonalTime />} />
        <Route path="schedule" element={<WorkerSchedule />} />
        <Route path="history" element={<WorkerHistory />} />
        <Route path="payslips" element={<WorkerPayslips />} />
      </Route>

      {/* Operio Sales Portal */}
      <Route
        path="/sales"
        element={
          <PrivateRoute allowedRoles={['sales', 'superadmin']}>
            <SalesLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<SalesDashboard />} />
        <Route path="new-customer" element={<SalesNewCustomer />} />
        <Route path="customers" element={<SalesCustomers />} />
      </Route>
      {/* 404 Catch-all */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
